import { i18n } from '@/shared/i18n';
import type { PipelineStepId, BriefSubmitPayload } from '../model/brief-generation.types';

export type DisplayStepId =
  | 'analyze-instructions'
  | 'read-files'
  | 'review-artifacts'
  | 'project-context'
  | 'check-kb'
  | 'generate-brief';

export interface DisplayStep {
  id: DisplayStepId;
  label: string;
  pipelineIds: readonly PipelineStepId[];
}

// `i18n.t` rather than a `t` from the caller: this builder is invoked directly
// (including from tests), so it must resolve labels without a hook in scope.
export const buildDisplaySteps = (payload: BriefSubmitPayload | null): DisplayStep[] => {
  const steps: DisplayStep[] = [];

  steps.push({
    id: 'analyze-instructions',
    label: i18n.t('briefs:briefGeneration.buildDisplaySteps.analyzeYourInstructions'),
    // `reading-files` only counts towards this row when something was uploaded. A run with no
    // files never reports that step at all on the workspace-api host, which skips the extraction
    // agent outright, and a row waits for every id it names, so including it unconditionally left
    // the first row unfinished for the whole run.
    pipelineIds: payload?.files.length
      ? ['reading-files', 'analyzing-inputs']
      : ['analyzing-inputs'],
  });

  if (payload?.files.length) {
    steps.push({
      id: 'read-files',
      label: i18n.t('briefs:briefGeneration.buildDisplaySteps.readUploadedFiles'),
      pipelineIds: ['reading-files'],
    });
  }

  if (payload?.artifactIds.length) {
    steps.push({
      id: 'review-artifacts',
      label: i18n.t('briefs:briefGeneration.buildDisplaySteps.reviewSelectedArtifacts'),
      pipelineIds: ['analyzing-inputs'],
    });
  }

  if (payload?.projectContext) {
    steps.push({
      id: 'project-context',
      label: i18n.t('briefs:briefGeneration.buildDisplaySteps.lookAtProjectContext'),
      pipelineIds: ['analyzing-inputs'],
    });
  }

  if (payload?.knowledgeBaseIds.length) {
    steps.push({
      id: 'check-kb',
      label: i18n.t('briefs:briefGeneration.buildDisplaySteps.checkSelectedKnowledgeBase'),
      pipelineIds: ['checking-knowledge-base'],
    });
  }

  steps.push({
    id: 'generate-brief',
    label: i18n.t('briefs:briefGeneration.buildDisplaySteps.generateYourBrief'),
    pipelineIds: ['composing-brief', 'saving-brief'],
  });

  return steps;
};
